import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Button, Col, Row, Typography, theme } from 'antd';
import {
  ArrowDownOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  FIFO_PREVIEW_POLICY,
  type PagoFifoPreviewInvoice,
  type PagoFifoPreviewResponse,
} from '@aid/shared';
import { formatCurrency } from '../../../lib/format';
import { useT } from '../../../locales';
import dayjs from 'dayjs';

const { Text } = Typography;
const UNCHANGED_INVOICES_ID = 'fifo-unchanged-invoices';

const NOTICE_TONE = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
} as const;

type NoticeTone = (typeof NOTICE_TONE)[keyof typeof NOTICE_TONE];

interface PagoFifoPreviewProps {
  preview: PagoFifoPreviewResponse | undefined;
  hasAmount: boolean;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

interface PreviewNoticeProps {
  tone: NoticeTone;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}

interface InvoiceMetricProps {
  label: string;
  value: string;
  emphasized?: boolean;
}

interface InvoiceListProps {
  invoices: PagoFifoPreviewInvoice[];
  ariaLabel: string;
  id?: string;
}

interface TransformationStepProps {
  label: string;
  value: string;
  color: string;
  detail?: ReactNode;
}

function formatPeriod(invoice: PagoFifoPreviewInvoice): string {
  const start = dayjs(invoice.fechaInicio).format('DD/MM/YYYY');
  const end = dayjs(invoice.fechaFin).format('DD/MM/YYYY');
  return `${start} – ${end}`;
}

function hasAppliedAmount(invoice: PagoFifoPreviewInvoice): boolean {
  return Number(invoice.montoAplicado) > 0;
}

function TransformationStep({ label, value, color, detail }: TransformationStepProps) {
  return (
    <div style={{ minWidth: 0, padding: '6px 8px', textAlign: 'center' }}>
      <Text
        type="secondary"
        style={{ display: 'block', fontSize: 12, lineHeight: 1.35, marginBottom: 5 }}
      >
        {label}
      </Text>
      <Text
        strong
        style={{
          color,
          display: 'block',
          fontSize: 18,
          lineHeight: 1.25,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatCurrency(value)}
      </Text>
      {detail ? (
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: 12, lineHeight: 1.4, marginTop: 4 }}
        >
          {detail}
        </Text>
      ) : null}
    </div>
  );
}

function InvoiceMetric({ label, value, emphasized = false }: InvoiceMetricProps) {
  const { token } = theme.useToken();
  const isZero = Number(value) === 0;
  const valueColor = isZero
    ? token.colorTextSecondary
    : emphasized
      ? token.colorSuccess
      : token.colorText;

  return (
    <div style={{ minWidth: 0 }}>
      <Text
        type="secondary"
        style={{
          color: token.colorTextTertiary,
          display: 'block',
          fontSize: 11,
          lineHeight: 1.25,
          marginBottom: 4,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Text>
      <Text
        strong={emphasized && !isZero}
        style={{
          color: valueColor,
          display: 'block',
          fontSize: emphasized && !isZero ? 14 : 13,
          fontWeight: emphasized && !isZero ? 600 : undefined,
          lineHeight: 1.3,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatCurrency(value)}
      </Text>
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: PagoFifoPreviewInvoice }) {
  const t = useT();
  const { token } = theme.useToken();
  const applied = hasAppliedAmount(invoice);

  return (
    <div
      role="listitem"
      style={{ borderBottom: `1px solid ${token.colorSplit}`, padding: '12px 8px' }}
    >
      <Row gutter={[12, 8]} align="middle">
        <Col xs={24} sm={9} style={{ minWidth: 0 }}>
          <Text strong style={{ display: 'block', lineHeight: 1.3 }}>
            {t('pages.pagos.fifoPreview.invoice', { id: invoice.id })}
          </Text>
          <Text
            type="secondary"
            style={{
              color: token.colorTextSecondary,
              display: 'block',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
            }}
          >
            {formatPeriod(invoice)}
          </Text>
        </Col>
        <Col xs={8} sm={5} style={{ minWidth: 0 }}>
          <InvoiceMetric label={t('pages.pagos.fifoPreview.before')} value={invoice.saldoAntes} />
        </Col>
        <Col xs={8} sm={5} style={{ minWidth: 0 }}>
          <InvoiceMetric
            label={t('pages.pagos.fifoPreview.applied')}
            value={invoice.montoAplicado}
            emphasized={applied}
          />
        </Col>
        <Col xs={8} sm={5} style={{ minWidth: 0 }}>
          <InvoiceMetric label={t('pages.pagos.fifoPreview.after')} value={invoice.saldoDespues} />
        </Col>
      </Row>
    </div>
  );
}

function InvoiceList({ invoices, ariaLabel, id }: InvoiceListProps) {
  return (
    <div
      id={id}
      role="list"
      aria-label={ariaLabel}
      style={{ display: 'grid', gap: 8, padding: '4px 0' }}
    >
      {invoices.map((invoice) => (
        <InvoiceRow key={invoice.id} invoice={invoice} />
      ))}
    </div>
  );
}

function PreviewNotice({ tone, title, description, action }: PreviewNoticeProps) {
  const { token } = theme.useToken();
  const colors: Record<NoticeTone, { background: string; border: string; foreground: string }> = {
    [NOTICE_TONE.INFO]: {
      background: token.colorInfoBg,
      border: token.colorInfoBorder,
      foreground: token.colorInfo,
    },
    [NOTICE_TONE.SUCCESS]: {
      background: token.colorSuccessBg,
      border: token.colorSuccessBorder,
      foreground: token.colorSuccess,
    },
    [NOTICE_TONE.WARNING]: {
      background: token.colorWarningBg,
      border: token.colorWarningBorder,
      foreground: token.colorWarning,
    },
    [NOTICE_TONE.ERROR]: {
      background: token.colorErrorBg,
      border: token.colorErrorBorder,
      foreground: token.colorError,
    },
  };
  const palette = colors[tone];
  const icon =
    tone === NOTICE_TONE.SUCCESS ? (
      <CheckCircleOutlined aria-hidden="true" />
    ) : tone === NOTICE_TONE.WARNING ? (
      <WarningOutlined aria-hidden="true" />
    ) : tone === NOTICE_TONE.ERROR ? (
      <WarningOutlined aria-hidden="true" />
    ) : (
      <InfoCircleOutlined aria-hidden="true" />
    );

  return (
    <div
      role={tone === NOTICE_TONE.ERROR ? 'alert' : 'status'}
      aria-live={tone === NOTICE_TONE.ERROR ? 'assertive' : 'polite'}
      style={{
        alignItems: 'flex-start',
        background: palette.background,
        border: `1px solid ${palette.border}`,
        borderRadius: token.borderRadiusSM,
        display: 'flex',
        gap: 8,
        padding: '10px 12px',
      }}
    >
      <span aria-hidden="true" style={{ color: palette.foreground, lineHeight: 1.5 }}>
        {icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ color: palette.foreground, display: 'block', lineHeight: 1.4 }}>
          {title}
        </Text>
        {description ? (
          <Text style={{ color: token.colorText, display: 'block', fontSize: 12, lineHeight: 1.4 }}>
            {description}
          </Text>
        ) : null}
      </div>
      {action ? <div style={{ flex: '0 0 auto' }}>{action}</div> : null}
    </div>
  );
}

function PreviewBody({ preview, hasAmount, loading, error, onRetry }: PagoFifoPreviewProps) {
  const t = useT();
  const { token } = theme.useToken();
  const [showUnchanged, setShowUnchanged] = useState(false);
  const previewWrapperStyle: CSSProperties = {
    display: 'grid',
    gap: 16,
    marginBlock: token.marginSM,
    minWidth: 0,
  };

  useEffect(() => {
    setShowUnchanged(false);
  }, [preview?.idCliente, preview?.montoPago]);

  // The copy follows the policy the backend reported: the whole debt (FIFO) or
  // only the invoices the operator ticked.
  const esSeleccion = preview?.politica === FIFO_PREVIEW_POLICY.SELECTED_INVOICES_OLDEST_FIRST;
  const heading = (
    <div
      style={{
        background: token.colorFillAlter,
        border: `1px solid ${token.colorSplit}`,
        borderRadius: token.borderRadiusSM,
        display: 'grid',
        gap: 4,
        padding: 12,
      }}
    >
      <Text strong id="fifo-preview-heading" style={{ display: 'block' }}>
        {t(
          esSeleccion ? 'pages.pagos.fifoPreview.titleSeleccion' : 'pages.pagos.fifoPreview.title',
        )}
      </Text>
      <Text type="secondary" style={{ display: 'block', lineHeight: 1.5 }}>
        {t(esSeleccion ? 'pages.pagos.fifoPreview.ruleSeleccion' : 'pages.pagos.fifoPreview.rule')}
      </Text>
    </div>
  );

  if (!hasAmount) {
    return (
      <div style={previewWrapperStyle}>
        {heading}
        <PreviewNotice
          tone={NOTICE_TONE.INFO}
          title={t('pages.pagos.fifoPreview.waitingAmountTitle')}
          description={t('pages.pagos.fifoPreview.waitingAmountDescription')}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={previewWrapperStyle}>
        {heading}
        <PreviewNotice
          tone={NOTICE_TONE.INFO}
          title={t('pages.pagos.fifoPreview.loadingTitle')}
          description={t('pages.pagos.fifoPreview.loadingDescription')}
        />
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div style={previewWrapperStyle}>
        {heading}
        <PreviewNotice
          tone={NOTICE_TONE.ERROR}
          title={t('pages.pagos.fifoPreview.errorTitle')}
          description={t('pages.pagos.fifoPreview.errorDescription')}
          action={
            <Button type="link" size="small" onClick={onRetry}>
              {t('pages.pagos.fifoPreview.retry')}
            </Button>
          }
        />
      </div>
    );
  }

  const affectedInvoices = preview.facturas.filter(hasAppliedAmount);
  const unchangedInvoices = preview.facturas.filter((invoice) => !hasAppliedAmount(invoice));
  const generatedCredit = Number(preview.creditoGenerado);
  const paymentAmount = Number(preview.montoPago);
  const totalApplied = Number(preview.totalAplicado);
  const debtBefore = Number(preview.totalDeudaAntes);
  const debtAfter = Number(preview.deudaDespues);
  const noPendingInvoices = preview.facturas.length === 0;
  const exactSettlement = totalApplied > 0 && debtAfter === 0 && generatedCredit === 0;
  const partialSettlement = totalApplied > 0 && debtAfter > 0;
  const appliedDiffersFromPayment = Math.abs(totalApplied - paymentAmount) >= 0.005;
  const showAppliedAmount =
    totalApplied > 0 && (appliedDiffersFromPayment || debtAfter > 0 || generatedCredit > 0);

  const outcome = noPendingInvoices ? (
    <PreviewNotice
      tone={NOTICE_TONE.SUCCESS}
      title={t('pages.pagos.fifoPreview.noPendingTitle')}
      description={t('pages.pagos.fifoPreview.noPendingDescription', {
        amount: formatCurrency(preview.montoPago),
      })}
    />
  ) : generatedCredit > 0 ? (
    <PreviewNotice
      tone={NOTICE_TONE.WARNING}
      title={t('pages.pagos.fifoPreview.creditTitle')}
      description={t('pages.pagos.fifoPreview.creditDescription', {
        amount: formatCurrency(preview.creditoGenerado),
      })}
    />
  ) : exactSettlement ? (
    <PreviewNotice
      tone={NOTICE_TONE.SUCCESS}
      title={t('pages.pagos.fifoPreview.exactTitle')}
      description={t('pages.pagos.fifoPreview.exactDescription', {
        amount: formatCurrency(preview.totalDeudaAntes),
      })}
    />
  ) : partialSettlement ? (
    <PreviewNotice
      tone={NOTICE_TONE.INFO}
      title={t('pages.pagos.fifoPreview.partialTitle')}
      description={t('pages.pagos.fifoPreview.partialDescription', {
        applied: formatCurrency(preview.totalAplicado),
        remaining: formatCurrency(preview.deudaDespues),
      })}
    />
  ) : null;

  return (
    <div style={previewWrapperStyle}>
      {heading}

      <section aria-labelledby="fifo-impact-heading">
        <Text strong id="fifo-impact-heading" style={{ display: 'block', marginBottom: 8 }}>
          {t('pages.pagos.fifoPreview.transformationTitle')}
        </Text>
        <div
          role="group"
          aria-label={t('pages.pagos.fifoPreview.transformationLabel')}
          style={{
            borderBottom: `1px solid ${token.colorSplit}`,
            borderTop: `1px solid ${token.colorSplit}`,
            padding: '12px 0',
          }}
        >
          <Row gutter={[8, 8]} align="middle">
            <Col xs={24} sm={7} style={{ minWidth: 0 }}>
              <TransformationStep
                label={t('pages.pagos.fifoPreview.currentDebt')}
                value={preview.totalDeudaAntes}
                color={debtBefore > 0 ? token.colorError : token.colorTextSecondary}
              />
            </Col>
            <Col xs={24} sm={0} style={{ color: token.colorTextSecondary, textAlign: 'center' }}>
              <ArrowDownOutlined aria-hidden="true" />
            </Col>
            <Col xs={0} sm={1} style={{ color: token.colorTextSecondary, textAlign: 'center' }}>
              <ArrowRightOutlined aria-hidden="true" />
            </Col>
            <Col xs={24} sm={8} style={{ minWidth: 0 }}>
              <TransformationStep
                label={t('pages.pagos.fifoPreview.payment')}
                value={preview.montoPago}
                color={token.colorPrimary}
                detail={
                  showAppliedAmount
                    ? t('pages.pagos.fifoPreview.appliedToDebt', {
                        amount: formatCurrency(preview.totalAplicado),
                      })
                    : undefined
                }
              />
            </Col>
            <Col xs={24} sm={0} style={{ color: token.colorTextSecondary, textAlign: 'center' }}>
              <ArrowDownOutlined aria-hidden="true" />
            </Col>
            <Col xs={0} sm={1} style={{ color: token.colorTextSecondary, textAlign: 'center' }}>
              <ArrowRightOutlined aria-hidden="true" />
            </Col>
            <Col xs={24} sm={7} style={{ minWidth: 0 }}>
              <TransformationStep
                label={t('pages.pagos.fifoPreview.remainingDebt')}
                value={preview.deudaDespues}
                color={debtAfter > 0 ? token.colorError : token.colorSuccess}
              />
            </Col>
          </Row>
        </div>
      </section>

      {outcome}

      {affectedInvoices.length > 0 ? (
        <section aria-labelledby="fifo-affected-heading">
          <div
            style={{
              alignItems: 'baseline',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 12,
            }}
          >
            <Text strong id="fifo-affected-heading">
              {t('pages.pagos.fifoPreview.affectedHeading')}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('pages.pagos.fifoPreview.affectedCount', { count: affectedInvoices.length })}
            </Text>
          </div>
          <InvoiceList
            invoices={affectedInvoices}
            ariaLabel={t('pages.pagos.fifoPreview.invoiceDetails')}
          />
        </section>
      ) : null}

      {unchangedInvoices.length > 0 ? (
        <section aria-labelledby="fifo-unchanged-heading">
          <div
            style={{
              alignItems: 'center',
              borderTop: `1px solid ${token.colorSplit}`,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              justifyContent: 'space-between',
              padding: '4px 0',
            }}
          >
            <div style={{ alignItems: 'baseline', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <Text strong id="fifo-unchanged-heading">
                {t('pages.pagos.fifoPreview.unchangedHeading')}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t('pages.pagos.fifoPreview.unchangedCount', { count: unchangedInvoices.length })}
              </Text>
            </div>
            <Button
              type="link"
              size="small"
              aria-expanded={showUnchanged}
              aria-controls={UNCHANGED_INVOICES_ID}
              onClick={() => setShowUnchanged((visible) => !visible)}
              style={{ padding: 0 }}
            >
              {showUnchanged
                ? t('pages.pagos.fifoPreview.hideUnchanged')
                : t('pages.pagos.fifoPreview.showUnchanged', { count: unchangedInvoices.length })}
            </Button>
          </div>
          {showUnchanged ? (
            <InvoiceList
              id={UNCHANGED_INVOICES_ID}
              invoices={unchangedInvoices}
              ariaLabel={t('pages.pagos.fifoPreview.invoiceDetails')}
            />
          ) : null}
        </section>
      ) : null}

      <Text
        type="secondary"
        style={{ display: 'block', fontSize: 12, lineHeight: 1.5, marginTop: 4 }}
      >
        {t('pages.pagos.fifoPreview.advisory')}
      </Text>
    </div>
  );
}

export function PagoFifoPreview(props: PagoFifoPreviewProps) {
  return <PreviewBody {...props} />;
}
